/**
 * Carga y confirmacion de la sesion de checkout (plan 7.3.2 y 7.3.3).
 *
 * Autenticacion: clave publicable en `X-API-Key` y `client_secret` en
 * `X-Client-Secret`. El client_secret JAMAS viaja como query param (CONVENCIONES 2),
 * porque acabaria en los access logs de NGINX y en la cabecera Referer.
 *
 * El id del intent se DERIVA del propio client_secret (`pi_<id>_secret_<random>`).
 *
 * Polling: se re-consulta con backoff (2 s inicial, factor 1.5, tope 10 s) mientras el intent este
 * en PROCESSING, y tambien en REQUIRES_ACTION cuando una pantalla lo pide con `startPolling` —las
 * que se quedan solas: el QR, donde no hay vuelta del navegador, y el formulario embebido de BCI,
 * donde el aviso del SDK ajeno se midio que puede no llegar—. Se detiene en cuanto el estado es
 * terminal.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { OpenedInscription, PaymentIntent, ProblemDetails } from '../types/api';
import {
  ApiProblemError,
  invalidClientSecretProblem,
  invalidResponseProblem,
  networkProblem,
  normalizeOpenedInscription,
  normalizePaymentIntent,
  problemFromPayload,
} from '../types/api';
import { intentIdFromClientSecret } from './parseFragment';
import type { UiState } from './uiState';
import { isTerminalStatus, mapIntentToUiState } from './uiState';

/** Datos no sensibles que la estrategia de pasarela entrega al confirmar. */
export interface ConfirmPayload {
  /**
   * Pasarela elegida por el comprador en el selector. Viaja cuando el intent nacio SIN pasarela,
   * que es como un hub ofrece varios medios de pago: el comercio pide el cobro y quien decide con
   * que se paga es el comprador. El servidor la fija en ese confirm.
   */
  gateway_id?: string | undefined;
  /** Token emitido por los hosted fields / Bricks de la pasarela. */
  gateway_token?: string | undefined;
  /** Token del retorno de un flujo de redireccion (p. ej. `token_ws` de Webpay). */
  return_token?: string | undefined;
  /**
   * Parametros CRUDOS del retorno de la pasarela, con sus propios nombres.
   *
   * <p>Es el mismo canal que {@link ConfirmPayload.return_token} --los dos acaban en el
   * `gateway_params` de payment-api-- y existe aparte porque aquel traduce a UNA clave fija
   * (`token_ws`) y esto no traduce nada: la estrategia sabe como se llama lo que su pasarela pone en
   * la URL de vuelta y aqui lo manda tal cual. Lo usa BCI con su `resultIndicator`.
   *
   * <p><strong>Lo que viaje por aqui no decide nada, por definicion.</strong> Sale de la barra de
   * direcciones, o sea de un sitio que cualquiera puede escribir; el servidor lo contrasta contra su
   * propia copia y el desenlace lo firma la consulta a la pasarela. Nunca datos de tarjeta (PCI DSS
   * SAQ A).
   */
  gateway_params?: Record<string, string> | undefined;
}

/** Como quedo el cobro despues de que el comprador dejara la ventana del banco. */
export interface AbandonResult {
  intent: PaymentIntent;
  /**
   * El banco espera las firmas o la liberacion de fondos de una empresa: el comprador ya hizo su
   * parte y el desenlace llega despues, por webhook. El intent sigue en REQUIRES_ACTION igual que
   * uno sin tocar, asi que esto viaja aparte, en la cabecera `ApiPay-Pending-Reason`.
   */
  pendienteDeAutorizacion: boolean;
}

/** Cabecera de `/abandon` y el valor que significa «espera firmas de la empresa». */
export const PENDING_REASON_HEADER = 'ApiPay-Pending-Reason';
export const PENDING_AUTHORIZATION = 'authorization_pending';

export interface CheckoutSession {
  intent: PaymentIntent | null;
  uiState: UiState;
  problem: ProblemDetails | null;
  /**
   * Confirma el intent contra la pasarela.
   *
   * <p><strong>RECHAZA con {@link ApiProblemError} si no sale bien</strong>, igual que
   * {@link CheckoutSession.openInscription}. Esa es la unica senal que las estrategias pueden ver:
   * todas envuelven su confirm en un `catch` que pinta el aviso y devuelve el boton, y mientras
   * esto resolvia siempre —publicando el fallo en `problem`, que solo se pinta cuando la sesion NO
   * llego a cargar— ninguno de esos `catch` se ejecutaba jamas. El comprador se quedaba mirando
   * «redirigiendo» para siempre ante un rechazo, que es un desenlace normal.
   *
   * <p>Antes de rechazar RELEE el intent, porque un rechazo de pasarela lo mueve a FAILED en el
   * servidor y el cuerpo del error no lo trae.
   *
   * <p>⚠️ Cualquier doble de esta interfaz tiene que rechazar tambien. Un doble que resuelve donde
   * el original rechaza —o al reves— deja el hueco justo donde nadie mira: asi estuvo verde durante
   * meses la pantalla de fallo de las siete estrategias.
   */
  confirm(payload: ConfirmPayload): Promise<void>;
  /**
   * Abre una inscripcion de medio de pago PARA ESTE INTENT y devuelve el formulario de la pasarela.
   *
   * <p>Va sobre el intent y no sobre `/v1/payment-methods/inscriptions` porque esa ruta exige `sk_`
   * y el navegador solo tiene la clave publicable. Aqui la credencial es la del resto del checkout
   * —`pk_` mas `X-Client-Secret`—, de modo que la clave publicable no gana poderes generales: solo
   * puede inscribir para el intent cuyo `client_secret` ya demuestra tener.
   *
   * <p>Rechaza con {@link ApiProblemError} en vez de publicar el fallo en `problem`: el checkout
   * sigue en pie y la estrategia muestra el aviso junto al boton para que el comprador reintente.
   *
   * @param gatewayId pasarela con la que se inscribe (la activa de la sesion)
   */
  openInscription(gatewayId: string): Promise<OpenedInscription>;
  /**
   * Cierra la inscripcion y asocia la tarjeta resultante al intent, EN EL SERVIDOR.
   *
   * <p>Solo necesita el `ins_...`: el token con el que se cierra contra la pasarela lo guardo el
   * servidor al abrirla, asi que el navegador no tiene que capturar nada del retorno. Despues de
   * esto el {@link CheckoutSession.confirm} de siempre es el que cobra, porque el intent ya lleva
   * el medio de pago.
   */
  confirmInscription(inscriptionId: string): Promise<void>;
  /**
   * El comprador dejo la ventana del banco (hoy, la de Santander): el servidor le pregunta al banco
   * y decide. Pagado queda pagado; sin pagar queda CANCELADO y el comercio se entera al instante;
   * en curso (pendiente de firma de una empresa) no se toca.
   *
   * <p>Resuelve con el intent como quedo, o `null` si no se pudo avisar. Nunca rechaza: si el aviso
   * no llega, el cobro sigue como estaba y lo cierra la reconciliacion, que es lo que pasaba antes.
   */
  abandon(): Promise<AbandonResult | null>;
  startPolling(): void;
}

export interface UseCheckoutSessionArgs {
  clientSecret: string;
  publicKey: string;
  apiBaseUrl: string;
}

const POLL_INITIAL_MS = 2000;
const POLL_FACTOR = 1.5;
const POLL_MAX_MS = 10_000;

/** Clave con la que payment-api espera el token de retorno de Webpay Plus. */
const WEBPAY_RETURN_PARAM = 'token_ws';

/**
 * Cabecera de deduplicacion de las operaciones que TOCAN la pasarela.
 *
 * <p>Se manda aunque el confirm del intent no la exija: abrir una inscripcion abre un tramite en
 * Transbank, y un doble envio sin clave dejaria dos inscripciones abiertas de las que solo una se
 * cierra. Con clave, el reintento reproduce la primera.
 */
const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';

/** Cuerpo de la respuesta, o null cuando no hay JSON que leer (204, cuerpo roto, HTML de un proxy). */
async function readJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export function useCheckoutSession(args: UseCheckoutSessionArgs): CheckoutSession {
  const { clientSecret, publicKey, apiBaseUrl } = args;

  const [intent, setIntent] = useState<PaymentIntent | null>(null);
  const [problem, setProblem] = useState<ProblemDetails | null>(null);
  /**
   * Una pantalla PIDIO quedarse esperando el desenlace (ver `startPolling`).
   *
   * <p>Es lo que distingue «REQUIRES_ACTION porque el comprador se fue a la pasarela» —donde
   * sondear no sirve de nada, porque vuelve y su confirm decide— de «REQUIRES_ACTION con el
   * comprador delante de una pantalla que se quedo sola», donde preguntar es lo unico que puede
   * cerrar el cobro. Sola por dos motivos distintos, y los dos medidos: el QR de RutPay, que se
   * escanea con el telefono y no produce vuelta ninguna, y el formulario embebido de BCI, cuyo
   * unico aviso es un callback de un iframe ajeno que el 2026-09-17 no llego dos veces.
   */
  const [esperandoDesenlace, setEsperandoDesenlace] = useState(false);

  const mountedRef = useRef(true);
  const delayRef = useRef(POLL_INITIAL_MS);

  const intentId = useMemo(() => intentIdFromClientSecret(clientSecret), [clientSecret]);
  const baseUrl = useMemo(() => apiBaseUrl.replace(/\/+$/, ''), [apiBaseUrl]);

  const headers = useMemo<Record<string, string>>(
    () => ({
      Accept: 'application/json',
      'X-API-Key': publicKey,
      'X-Client-Secret': clientSecret,
    }),
    [publicKey, clientSecret],
  );

  const handleResponse = useCallback(async (response: Response): Promise<PaymentIntent | null> => {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    if (!mountedRef.current) {
      return null;
    }
    if (!response.ok) {
      setProblem(problemFromPayload(payload, response.status));
      return null;
    }
    const next = normalizePaymentIntent(payload);
    if (next === null) {
      setProblem(invalidResponseProblem());
      return null;
    }
    setIntent(next);
    setProblem(null);
    return next;
  }, []);

  const requestIntent = useCallback(async (): Promise<PaymentIntent | null> => {
    if (intentId === null) {
      if (mountedRef.current) {
        setProblem(invalidClientSecretProblem());
      }
      return null;
    }
    let response: Response;
    try {
      response = await fetch(`${baseUrl}/v1/payment-intents/${encodeURIComponent(intentId)}`, {
        method: 'GET',
        headers,
        credentials: 'omit',
        mode: 'cors',
        cache: 'no-store',
      });
    } catch {
      if (mountedRef.current) {
        setProblem(networkProblem());
      }
      return null;
    }
    return handleResponse(response);
  }, [baseUrl, handleResponse, headers, intentId]);

  const confirm = useCallback(
    async (payload: ConfirmPayload): Promise<void> => {
      if (intentId === null) {
        // Aqui SI se publica en `problem`, y es el unico caso: sin intent utilizable no hay
        // estrategia montada que pueda recoger el rechazo, y `problem` es lo que pinta la pantalla.
        if (mountedRef.current) {
          setProblem(invalidClientSecretProblem());
        }
        throw new ApiProblemError(invalidClientSecretProblem());
      }

      // El cuerpo solo transporta tokens emitidos por la pasarela: nunca datos de
      // tarjeta (PCI DSS SAQ A). `gateway_params` es el canal que payment-api abre
      // para el retorno de los flujos de redireccion.
      const body: Record<string, unknown> = {};
      const chosenGateway = payload.gateway_id;
      if (chosenGateway !== undefined && chosenGateway.length > 0) {
        body['gateway_id'] = chosenGateway;
      }
      const gatewayToken = payload.gateway_token;
      if (gatewayToken !== undefined && gatewayToken.length > 0) {
        body['payment_method_token'] = gatewayToken;
      }
      // Las DOS formas de llenar `gateway_params` se acumulan en el mismo mapa: la nombrada
      // (`return_token`, que traduce a la clave de Webpay) y la cruda, que ya viene con los nombres
      // de la pasarela. Escribirlas en dos asignaciones separadas dejaba que la segunda pisara a la
      // primera sin que nada lo dijera.
      const gatewayParams: Record<string, string> = {};
      const returnToken = payload.return_token;
      if (returnToken !== undefined && returnToken.length > 0) {
        gatewayParams[WEBPAY_RETURN_PARAM] = returnToken;
      }
      for (const [clave, valor] of Object.entries(payload.gateway_params ?? {})) {
        if (clave.length > 0 && valor.length > 0) {
          gatewayParams[clave] = valor;
        }
      }
      if (Object.keys(gatewayParams).length > 0) {
        body['gateway_params'] = gatewayParams;
      }

      /*
       * AQUI NO SE PUBLICA NINGUN «estoy ocupado», y es deliberado.
       *
       * Este hook llego a llevar una bandera `pending` que la carcasa traducia a la pantalla de
       * espera. Con ella, un confirm en vuelo TAPABA al flujo de pasarela --SessionPage pinta una
       * cosa u otra, no las dos-- y lo DESMONTABA con su propia peticion a medias. Lo que salia de
       * ahi, medido en QA el 2026-09-16 con Webpay caido, era un bucle sin fondo: el confirm
       * fallaba, su `catch` pintaba el aviso sobre un componente que ya no existia, al bajar la
       * bandera el flujo se remontaba, el `useRef` de «ya arranque» habia muerto con el, y el
       * auto-arranque volvia a empezar. Cada vuelta, otro golpe a la pasarela.
       *
       * El fallo de fondo era de significado: una peticion HTTP abierta AQUI no es un pago en
       * proceso. `PROCESSING` es el intent, en el servidor, con el dinero ya movido; el primer
       * confirm de un flujo de redireccion no confirma nada, pide la direccion a la que mandar al
       * comprador. Por eso tambien mentia la pantalla, que decia «Estamos confirmando tu pago»
       * antes de que el comprador hubiera ido a pagar.
       *
       * Quien esta ocupado es el flujo, y el flujo ya lo cuenta con su propia fase y su propio
       * texto. Lo unico que este hook publica es el estado del intent.
       */
      let response: Response;
      try {
        response = await fetch(
          `${baseUrl}/v1/payment-intents/${encodeURIComponent(intentId)}/confirm`,
          {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'omit',
            mode: 'cors',
            cache: 'no-store',
            body: JSON.stringify(body),
          },
        );
      } catch {
        // No se relee: la peticion no llego, el intent no ha podido cambiar, y un GET que tampoco
        // va a llegar solo anade espera antes del aviso.
        throw new ApiProblemError(networkProblem());
      }

      // Tras confirmar, el backoff vuelve a empezar: el commit server-side suele
      // resolverse en el primer o segundo sondeo.
      delayRef.current = POLL_INITIAL_MS;

      if (!response.ok) {
        const problema = problemFromPayload(await readJsonBody(response), response.status);
        // SE RELEE EL INTENT, y esto es la mitad del arreglo. El cuerpo de un error es un
        // problem+json, no el intent, asi que la copia del navegador se queda en el estado
        // anterior aunque el servidor ya haya decidido. Y decide: un rechazo de pasarela mueve el
        // intent a FAILED y solo DESPUES lanza el 402 desde el borde (ver el comentario de
        // `PaymentIntentService.confirmWithOutcome`, «UN RECHAZO NO ES UN FALLO DE INTEGRACION»).
        // Sin releer, el widget seguia creyendolo en CREATED y le volvia a ofrecer pagar contra un
        // intent terminal, que responde 409. Con el estado real, la carcasa pinta la pantalla
        // terminal, que es la que tiene reintento y vuelta a la tienda.
        //
        // Se relee en TODOS los codigos y no solo en el 402: un 409 significa exactamente lo mismo
        // --esta copia es vieja-- y en los demas un GET de mas no cuesta nada que valga la pena
        // ahorrar en un camino que ya fallo.
        await requestIntent();
        throw new ApiProblemError(problema);
      }

      const confirmado = await handleResponse(response);
      // Un 200 con un cuerpo que no es un intent tampoco es un exito. Rechazar aqui es lo que hace
      // cierta la regla de la que dependen las estrategias: un confirm que RESUELVE ha publicado
      // siempre un intent nuevo. `ConfirmOnReturnFlow` la usa para distinguir «todavia no ha
      // llegado la respuesta» de «llego y no traia a donde ir».
      if (confirmado === null && mountedRef.current) {
        throw new ApiProblemError(invalidResponseProblem());
      }
    },
    [baseUrl, handleResponse, headers, intentId, requestIntent],
  );

  /**
   * POST de una operacion de inscripcion sobre el intent.
   *
   * <p>No pasa por `handleResponse`: un fallo aqui NO es un fallo de la sesion. Se rechaza con
   * `ApiProblemError` y la estrategia decide, en vez de dejar el checkout en la pantalla de error.
   *
   * <p>PCI DSS SAQ A: por estas dos rutas no viaja ningun dato de tarjeta. El cuerpo lleva, como
   * mucho, el identificador de la pasarela; el numero se teclea en el formulario de Transbank.
   */
  const postInscription = useCallback(
    async (
      path: string,
      body: Record<string, unknown>,
      idempotencyKey: string,
    ): Promise<unknown> => {
      if (intentId === null) {
        throw new ApiProblemError(invalidClientSecretProblem());
      }
      let response: Response;
      try {
        response = await fetch(
          `${baseUrl}/v1/payment-intents/${encodeURIComponent(intentId)}${path}`,
          {
            method: 'POST',
            headers: {
              ...headers,
              'Content-Type': 'application/json',
              [IDEMPOTENCY_KEY_HEADER]: idempotencyKey,
            },
            credentials: 'omit',
            mode: 'cors',
            cache: 'no-store',
            body: JSON.stringify(body),
          },
        );
      } catch {
        throw new ApiProblemError(networkProblem());
      }
      const payload = await readJsonBody(response);
      if (!response.ok) {
        throw new ApiProblemError(problemFromPayload(payload, response.status));
      }
      return payload;
    },
    [baseUrl, headers, intentId],
  );

  const openInscription = useCallback(
    async (gatewayId: string): Promise<OpenedInscription> => {
      // El cuerpo lleva SOLO la pasarela, y los otros dos campos que la ruta acepta se omiten a
      // proposito:
      //
      //  - `customer_ref` no existe en ese cuerpo: lo deriva el servidor del intent
      //    (`metadata.customer_ref`, o su id publico). Que lo eligiera el navegador permitiria
      //    inscribir una tarjeta a nombre de otro cliente del comercio;
      //  - `return_url` se omite para que el servidor reutilice la del intent, que es por donde ya
      //    vuelve el pago —y donde el nginx del widget traduce el POST de Transbank a un 303—;
      //  - `email` se omite porque el navegador no lo tiene: el comprador no lo teclea en ninguna
      //    pantalla de este widget. Lo resuelve el servidor a partir del intent —el
      //    `customer_email` con el que lo creo el comercio, y si no `metadata.customer_email`—, y
      //    si no hay ninguno responde `validation_error`. Ese caso ya no deberia llegar aqui: la
      //    sesion de checkout omite las pasarelas que exigen tarjeta guardada cuando el intent no
      //    puede conseguir ninguna (`CheckoutSessionService.configFor`).
      //
      // Nada mas cabe aqui: el DTO de la ruta RECHAZA propiedades desconocidas, asi que un campo de
      // adorno no se ignora, sino que tumba la inscripcion con `validation_error`.
      //
      // La clave de idempotencia es obligatoria en esta ruta y lleva el instante del intento: dos
      // envios del mismo click caen en la misma y se reproducen, mientras que un reintento del
      // comprador segundos despues abre una inscripcion nueva, que es lo que quiere cuando la
      // anterior no llego a completarse.
      const payload = await postInscription(
        '/inscriptions',
        { gateway_id: gatewayId },
        `${intentId ?? ''}-inscription-${String(Date.now())}`,
      );
      const opened = normalizeOpenedInscription(payload);
      if (opened === null) {
        throw new ApiProblemError(invalidResponseProblem());
      }
      return opened;
    },
    [intentId, postInscription],
  );

  const confirmInscription = useCallback(
    async (inscriptionId: string): Promise<void> => {
      // Clave estable por inscripcion: recargar la pagina de retorno reproduce el cierre en vez de
      // intentar cerrar dos veces la misma inscripcion, que la pasarela ya rechazaria.
      const payload = await postInscription(
        `/inscriptions/${encodeURIComponent(inscriptionId)}/confirm`,
        {},
        `${inscriptionId}-confirm`,
      );
      // La respuesta es el INTENT ya con la tarjeta asociada, y no el medio de pago: el navegador
      // nunca sostiene un `pm_`. Se adopta tal cual, que es una lectura gratis del estado nuevo.
      //
      // Si un dia no fuera un intent utilizable, tampoco es un error: la tarjeta quedo asociada
      // igualmente y el confirm que viene despues relee el intent. Lo que no puede es marcar la
      // sesion como rota.
      const next = normalizePaymentIntent(payload);
      if (next !== null && mountedRef.current) {
        setIntent(next);
        setProblem(null);
      }
    },
    [postInscription],
  );

  const abandon = useCallback(async (): Promise<AbandonResult | null> => {
    if (intentId === null) {
      return null;
    }
    let response: Response;
    try {
      response = await fetch(
        `${baseUrl}/v1/payment-intents/${encodeURIComponent(intentId)}/abandon`,
        {
          method: 'POST',
          headers,
          credentials: 'omit',
          mode: 'cors',
          cache: 'no-store',
        },
      );
    } catch {
      return null;
    }
    if (!response.ok) {
      // No es un fallo de la sesion: el cobro sigue como estaba y el sondeo lo vera cerrarse.
      return null;
    }
    const pendienteDeAutorizacion =
      response.headers.get(PENDING_REASON_HEADER) === PENDING_AUTHORIZATION;
    const quedo = await handleResponse(response);
    return quedo === null ? null : { intent: quedo, pendienteDeAutorizacion };
  }, [baseUrl, handleResponse, headers, intentId]);

  const startPolling = useCallback((): void => {
    delayRef.current = POLL_INITIAL_MS;
    setEsperandoDesenlace(true);
    void requestIntent();
  }, [requestIntent]);

  // Carga inicial del intent.
  useEffect(() => {
    mountedRef.current = true;
    void requestIntent();
    return (): void => {
      mountedRef.current = false;
    };
  }, [requestIntent]);

  /*
   * Bucle de polling: cada respuesta produce un objeto nuevo, asi que este efecto se reprograma
   * solo. Se agenda en dos casos, y la diferencia importa:
   *
   *  - PROCESSING: siempre. El cobro esta en vuelo y se resuelve en segundos.
   *  - REQUIRES_ACTION: SOLO si alguien lo pidio con `startPolling`. Por defecto no, y esa decision
   *    de origen es correcta: en los flujos de redireccion ahi manda la pasarela y el navegador se
   *    ha ido de esta pagina, asi que sondear seria gastar por nada.
   *
   * Lo que faltaba era el «solo si alguien lo pidio». `startPolling` hacia UNA relectura, asi que
   * una pantalla que se queda esperando --el QR de RutPay, donde no hay vuelta del navegador y
   * preguntar es lo unico que puede cerrar el cobro-- preguntaba una vez y se callaba para siempre.
   * Medido en QA el 2026-09-15: el comprador pago con la app de BancoEstado y la pantalla se quedo
   * en «Esperando que completes el pago» sin enterarse nunca.
   *
   * El coste de sostenerlo: un GET cada 10 s como mucho mientras esa pantalla siga abierta. Lo
   * acota el propio desenlace --al llegar a terminal se para y la bandera se baja-- y, si el
   * comprador abandona, la expiracion del intent, que tambien es terminal.
   */
  useEffect(() => {
    if (intent === null) {
      delayRef.current = POLL_INITIAL_MS;
      return;
    }
    // Un estado terminal para el sondeo ANTES de mirar la bandera, y por eso la bandera no se baja
    // aqui: bajarla no cambiaria nada --de terminal no se sale-- y un guardia que no puede fallar
    // es peor que ninguno, porque se lee como si vigilara algo.
    if (isTerminalStatus(intent.status)) {
      delayRef.current = POLL_INITIAL_MS;
      return;
    }
    if (intent.status !== 'PROCESSING' && !esperandoDesenlace) {
      delayRef.current = POLL_INITIAL_MS;
      return;
    }
    const delay = delayRef.current;
    delayRef.current = Math.min(Math.round(delay * POLL_FACTOR), POLL_MAX_MS);
    const timer = setTimeout(() => {
      void requestIntent();
    }, delay);
    return (): void => {
      clearTimeout(timer);
    };
  }, [esperandoDesenlace, intent, requestIntent]);

  /*
   * El estado de la UI sale del INTENT y de nada mas.
   *
   * Sin intent la pantalla es la carga, o el error si la carga ya fallo; con intent manda el mapeo
   * canonico. No hay tercera fuente: lo que este hook tenga en vuelo no es una pantalla, y meterlo
   * aqui es lo que desmontaba al flujo de pasarela a media peticion (ver el comentario de
   * `confirm`). La regla que sostiene esto, y que no se negocia: mientras un flujo esta montado y
   * tiene el control, la carcasa no le cambia la pantalla debajo.
   */
  const uiState = useMemo<UiState>(() => {
    if (intent === null) {
      return problem === null ? 'loading' : 'failed';
    }
    return mapIntentToUiState(intent);
  }, [intent, problem]);

  return useMemo<CheckoutSession>(
    () => ({
      intent,
      uiState,
      problem,
      confirm,
      openInscription,
      confirmInscription,
      abandon,
      startPolling,
    }),
    [abandon, confirm, confirmInscription, intent, openInscription, problem, startPolling, uiState],
  );
}
